import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, candidateApi, type SavedJobItem } from '../lib/apiClient';
import { useAuth } from './AuthContext';

/**
 * Caches the candidate's saved-jobs list app-wide so the JobDetailPage heart
 * indicator and the My Saved Jobs page both share a single source of truth and
 * the heart's state is correct on first render without an extra round-trip.
 *
 * Loaded once at app boot (after auth resolves to a candidate). Mutations are
 * optimistic: save / unsave update the local cache immediately and only revert
 * on server failure. Anonymous users get an empty cache and any save attempt
 * surfaces the existing signin redirect at the call site.
 */

interface SavedJobsApi {
  /** Hydrated list, or `null` while still loading after auth resolves. */
  savedJobs: SavedJobItem[] | null;
  isJobSaved: (jobId: string) => boolean;
  /** Saves the job (idempotent). Resolves with the new saved_at timestamp. */
  saveJob: (jobId: string) => Promise<void>;
  /** Removes the job from the saved list. Idempotent on missing rows. */
  unsaveJob: (jobId: string) => Promise<void>;
  /** Re-fetches the list from the server (after a heavy mutation). */
  refresh: () => Promise<void>;
}

const SavedJobsContext = createContext<SavedJobsApi | null>(null);

export function useSavedJobs(): SavedJobsApi {
  const ctx = useContext(SavedJobsContext);
  if (!ctx) throw new Error('useSavedJobs must be used within <SavedJobsProvider>');
  return ctx;
}

export function SavedJobsProvider({ children }: { children: ReactNode }) {
  const { candidate } = useAuth();
  const [savedJobs, setSavedJobs] = useState<SavedJobItem[] | null>(null);
  const lastCandidateIdRef = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    if (!candidate) {
      setSavedJobs([]);
      return;
    }
    try {
      // Backend caps page_size at 100; bookmarks per candidate are expected to stay in
      // the dozens, so one page is enough. (Going over the cap was the root cause of a
      // bug where a successful save would clear the cache: refresh hit VALIDATION_FAILED,
      // the catch silently set [] and the heart un-filled.)
      const env = await candidateApi.savedJobs.list({ page_size: 100 });
      setSavedJobs(env.data);
    } catch (err) {
      // Silent failure — saved-jobs cache is best-effort. The heart will show
      // unsaved by default; the My Saved Jobs page surfaces its own error.
      if (!(err instanceof ApiError)) throw err;
      setSavedJobs([]);
    }
  }, [candidate]);

  useEffect(() => {
    const currentId = candidate?.id ?? null;
    if (lastCandidateIdRef.current === currentId) return;
    lastCandidateIdRef.current = currentId;
    if (!candidate) {
      setSavedJobs([]);
      return;
    }
    setSavedJobs(null);
    void refresh();
  }, [candidate, refresh]);

  const isJobSaved = useCallback(
    (jobId: string): boolean => {
      if (!savedJobs) return false;
      return savedJobs.some((s) => s.job_id === jobId);
    },
    [savedJobs],
  );

  const saveJob = useCallback(
    async (jobId: string) => {
      if (!candidate) throw new ApiError({ code: 'AUTH_INVALID', message: 'Sign in to save jobs.' }, 401);
      // Optimistic insert with placeholder job payload — refresh in background
      // pulls the server-truth job snapshot used by My Saved Jobs.
      const optimistic: SavedJobItem = {
        job_id: jobId,
        saved_at: new Date().toISOString(),
        job: null,
      };
      setSavedJobs((prev) => {
        const list = prev ?? [];
        if (list.some((s) => s.job_id === jobId)) return list;
        return [optimistic, ...list];
      });
      try {
        await candidateApi.savedJobs.save(jobId);
        // Refresh in the background to pick up the full job snapshot.
        void refresh();
      } catch (err) {
        // Revert on failure.
        setSavedJobs((prev) => (prev ?? []).filter((s) => s.job_id !== jobId));
        throw err;
      }
    },
    [candidate, refresh],
  );

  const unsaveJob = useCallback(
    async (jobId: string) => {
      const previous = savedJobs;
      setSavedJobs((prev) => (prev ?? []).filter((s) => s.job_id !== jobId));
      try {
        await candidateApi.savedJobs.unsave(jobId);
      } catch (err) {
        // Revert on failure.
        if (previous) setSavedJobs(previous);
        throw err;
      }
    },
    [savedJobs],
  );

  const value = useMemo<SavedJobsApi>(
    () => ({ savedJobs, isJobSaved, saveJob, unsaveJob, refresh }),
    [savedJobs, isJobSaved, saveJob, unsaveJob, refresh],
  );

  return <SavedJobsContext.Provider value={value}>{children}</SavedJobsContext.Provider>;
}
